import {
  AccountBalanceWalletRounded,
  ReceiptLongRounded,
  TrendingDownRounded,
  TrendingUpRounded,
  WarningAmberRounded
} from "@mui/icons-material";
import {
  Autocomplete,
  FormControl,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { FiBarChart2 } from "react-icons/fi";
import { Link } from "react-router-dom";
import {
  CompanyInterface,
  FinancialByCategoryResponse,
  FinancialCashFlowResponse,
  FinancialSummaryResponse
} from "../../common/interfaces";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import {
  FinancialByCategoryChart,
  FinancialCashFlowChart
} from "../../components/charts/FinancialCharts";
import InfoBanner from "../../components/InfoBanner";
import { ToastError } from "../../components/toaster/toast.utils";
import { useAuth } from "../../hooks/useAuth";
import api from "../../services/api";
import {
  ChartsGrid,
  Container,
  PickCompanyState,
  SectionLabel,
  StatBody,
  StatCard,
  StatGrid,
  StatIcon,
  TabsRow,
  Title,
  ToolbarRow
} from "./financial.styled";

const brl = (n: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(n || 0);

const accents = {
  emerald: "#34d399",
  rose: "#fb7185",
  indigo: "#2b7bff",
  amber: "#fbbf24"
};

type RangeKey = "this_month" | "last_month" | "3m" | "6m" | "12m" | "custom";

const toApiDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function resolveRange(key: RangeKey): { from: string; to: string } {
  const now = new Date();
  if (key === "this_month") {
    return {
      from: toApiDate(new Date(now.getFullYear(), now.getMonth(), 1)),
      to: toApiDate(new Date(now.getFullYear(), now.getMonth() + 1, 0))
    };
  }
  if (key === "last_month") {
    return {
      from: toApiDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      to: toApiDate(new Date(now.getFullYear(), now.getMonth(), 0))
    };
  }
  const monthsBack = key === "3m" ? 2 : key === "6m" ? 5 : 11;
  return {
    from: toApiDate(
      new Date(now.getFullYear(), now.getMonth() - monthsBack, 1)
    ),
    to: toApiDate(new Date(now.getFullYear(), now.getMonth() + 1, 0))
  };
}

const FinancialOverview: React.FC = () => {
  const { isAdmin } = useAuth();

  const [companies, setCompanies] = useState<CompanyInterface[]>([]);
  const [selectedCompany, setSelectedCompany] =
    useState<CompanyInterface | null>(null);

  const [rangeKey, setRangeKey] = useState<RangeKey>("this_month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [basis, setBasis] = useState<"cash" | "accrual">("cash");

  const [summary, setSummary] = useState<FinancialSummaryResponse | null>(null);
  const [cashFlow, setCashFlow] = useState<FinancialCashFlowResponse | null>(
    null
  );
  const [byCategory, setByCategory] =
    useState<FinancialByCategoryResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const { from, to } = useMemo(() => {
    if (rangeKey === "custom" && customFrom && customTo) {
      return { from: customFrom, to: customTo };
    }
    return resolveRange(rangeKey);
  }, [rangeKey, customFrom, customTo]);

  const groupBy =
    rangeKey === "this_month" || rangeKey === "last_month" ? "day" : "month";

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/company/admin")
      .then((r) => setCompanies(r.data.items ?? r.data ?? []))
      .catch((error) => ToastError(extractErrorMessage(error)));
  }, [isAdmin]);

  // ADMIN sem empresa escolhida não tem o que somar: o backend devolve vazio
  // de propósito, então nem vale disparar as três chamadas.
  const waitingCompany = isAdmin && !selectedCompany;

  useEffect(() => {
    if (waitingCompany) {
      setSummary(null);
      setCashFlow(null);
      setByCategory(null);
      setLoading(false);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const params = {
          from,
          to,
          basis,
          company_id: selectedCompany?.company_id
        };
        const [summaryRes, cashFlowRes, byCategoryRes] = await Promise.all([
          api.get<FinancialSummaryResponse>("/financial/summary", { params }),
          api.get<FinancialCashFlowResponse>("/financial/cash-flow", {
            params: { ...params, groupBy }
          }),
          api.get<FinancialByCategoryResponse>("/financial/by-category", {
            params: { ...params, type: "EXPENSE" }
          })
        ]);
        if (!mounted) return;
        setSummary(summaryRes.data);
        setCashFlow(cashFlowRes.data);
        setByCategory(byCategoryRes.data);
      } catch (error) {
        ToastError(extractErrorMessage(error));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, basis, groupBy, selectedCompany, waitingCompany]);

  const stats = [
    {
      label: "Entradas",
      value: brl(summary?.income ?? 0),
      icon: <TrendingUpRounded />,
      accent: accents.emerald
    },
    {
      label: "Saídas",
      value: brl(summary?.expense ?? 0),
      icon: <TrendingDownRounded />,
      accent: accents.rose
    },
    {
      label: "Saldo do período",
      value: brl(summary?.balance ?? 0),
      icon: <AccountBalanceWalletRounded />,
      accent: accents.indigo
    },
    {
      label: "Despesas pendentes",
      value: brl(summary?.pendingExpense ?? 0),
      icon: <ReceiptLongRounded />,
      accent: accents.amber
    },
    {
      label: "Despesas vencidas",
      value: brl(summary?.overdueExpense ?? 0),
      icon: <WarningAmberRounded />,
      accent: accents.rose
    }
  ];

  return (
    <Container>
      <Title>Financeiro</Title>

      <TabsRow>
        <Link to="/financial" className="active">
          Visão geral
        </Link>
        <Link to="/financial/entries">Lançamentos</Link>
        <Link to="/financial/categories">Categorias</Link>
      </TabsRow>

      <InfoBanner icon={<FiBarChart2 size={26} />} title="Visão geral">
        Resumo consolidado das entradas, saídas e saldo da empresa no
        período — leitura rápida da saúde financeira. Para editar um
        lançamento, use a aba Lançamentos.
      </InfoBanner>

      <ToolbarRow>
        {isAdmin && (
          <Autocomplete
            size="small"
            options={companies}
            sx={{ minWidth: 240 }}
            getOptionLabel={(opt) => opt.company_name || ""}
            isOptionEqualToValue={(option, value) =>
              option?.company_id === value?.company_id
            }
            value={selectedCompany}
            onChange={(_, v) => setSelectedCompany(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Empresa"
                // helperText={
                //   selectedCompany ? undefined : "Escolha uma empresa para ver"
                // }
              />
            )}
          />
        )}

        <FormControl size="small" style={{ minWidth: 180 }}>
          <Select
            value={rangeKey}
            onChange={(e) => setRangeKey(e.target.value as RangeKey)}
          >
            <MenuItem value="this_month">Este mês</MenuItem>
            <MenuItem value="last_month">Mês anterior</MenuItem>
            <MenuItem value="3m">Últimos 3 meses</MenuItem>
            <MenuItem value="6m">Últimos 6 meses</MenuItem>
            <MenuItem value="12m">Últimos 12 meses</MenuItem>
            <MenuItem value="custom">Personalizado</MenuItem>
          </Select>
        </FormControl>

        {rangeKey === "custom" && (
          <>
            <TextField
              size="small"
              label="De"
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="Até"
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </>
        )}

        <FormControl size="small" style={{ minWidth: 160 }}>
          <Select
            value={basis}
            onChange={(e) => setBasis(e.target.value as "cash" | "accrual")}
          >
            <MenuItem value="cash">Regime de caixa</MenuItem>
            <MenuItem value="accrual">Regime de competência</MenuItem>
          </Select>
        </FormControl>
      </ToolbarRow>

      {waitingCompany ? (
        <PickCompanyState>
          <strong>Escolha uma empresa</strong>
          Os números do financeiro são de uma empresa por vez. Somar caixa de
          empresas diferentes daria um total que não existe em lugar nenhum.
        </PickCompanyState>
      ) : (
        <>
          <SectionLabel>
            <h3>Resumo</h3>
          </SectionLabel>

          <StatGrid>
            {stats.map((s, i) => (
              <StatCard key={s.label} accent={s.accent} delay={i}>
                <StatIcon accent={s.accent}>{s.icon}</StatIcon>
                <StatBody>
                  <label>{s.label}</label>
                  <h2>{loading ? "…" : s.value}</h2>
                </StatBody>
              </StatCard>
            ))}
          </StatGrid>

          <ChartsGrid>
            <div className="span-8">
              <FinancialCashFlowChart data={cashFlow} loading={loading} />
            </div>
            <div className="span-4">
              <FinancialByCategoryChart data={byCategory} loading={loading} />
            </div>
          </ChartsGrid>
        </>
      )}
    </Container>
  );
};

export default FinancialOverview;
